// The row drawer. It shows what the row already carries, and the opportunity's touch history, which
// `TouchHistory.tsx` reads when the drawer opens.

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import type { LeadershipNextStep, Opportunity } from '../api/types.ts';
import type { TokenGetter } from '../api/client.ts';
import {
  formatAge, formatDate, hasAmount, shortOpportunityName, textLines, withoutLeadingDate,
} from '../board/format.ts';
import { PIPELINE_SOURCE_LABELS } from '../board/money.ts';
import { opportunityUrl } from '../board/salesforce.ts';
import { MoneySection } from './Money.tsx';
import { OwnersSection } from './Owners.tsx';
import { CHANNEL_LABELS, Dollars } from './PipelineTable.tsx';
import { TouchHistorySection } from './TouchHistory.tsx';
import { LeadershipNextStepSection } from './LeadershipNextStep.tsx';

function Fact({
  label, value, detail, late, wide, full,
}: {
  label: string;
  value: ReactNode;
  detail?: string;
  late?: boolean;
  wide?: boolean;
  full?: boolean;
}) {
  const className = full ? 'fact-full' : wide ? 'fact-wide' : undefined;
  return (
    <div className={className}>
      <dt>{label}</dt>
      <dd className={late ? 'level-bad' : undefined}>{value}</dd>
      {detail ? <dd className="fact-detail">{detail}</dd> : null}
    </div>
  );
}

// A dollar figure, or "—" when it's missing or $0 (nobody entered one), as the table shows it.
function MoneyValue({ value }: { value: number | null }) {
  return hasAmount(value) ? <Dollars amount={value} /> : <>—</>;
}

function SalesforceButton({ href }: { href: string | null }) {
  if (href) {
    return <a className="button button-primary" href={href} target="_blank" rel="noreferrer">Open in Salesforce ↗</a>;
  }
  return (
    <button type="button" className="button button-primary" disabled title="Needs the Salesforce address in the board's data, which isn't sent yet">
      Open in Salesforce ↗
    </button>
  );
}

export function Drawer({
  opportunity, today, instanceUrl, getToken, onClose, onLeadershipSaved,
}: {
  opportunity: Opportunity;
  today: string;
  instanceUrl: string | null;
  getToken: TokenGetter;
  onClose: () => void;
  onLeadershipSaved: (oppId: string, value: LeadershipNextStep | null) => void;
}) {
  const closeButton = useRef<HTMLButtonElement>(null);

  // Focus moves into the drawer on open and back to the row on close; Escape closes it.
  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButton.current?.focus();
    const onKeyDown = (event: globalThis.KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previousFocus?.focus();
    };
  }, [onClose]);

  const touch = opportunity.last_touch;
  const nextStep = opportunity.next_step;
  const gong = opportunity.gong_next_step;
  // `by_owner` means the row's rep made it (GRO-245); otherwise name who did.
  const touchBy = touch && !touch.by_owner ? ` · by ${touch.by?.name ?? 'someone other than the rep'}` : '';
  const pipelineSource = opportunity.pipeline_source;
  const pipelineDetail = pipelineSource && hasAmount(opportunity.pipeline_value) ? `from ${PIPELINE_SOURCE_LABELS[pipelineSource]}` : undefined;
  const touchValue = touch ? `${formatAge(touch.date, today)} · ${CHANNEL_LABELS[touch.channel]}` : 'Never';
  const repStepLines = nextStep.source === 'rep' && nextStep.text ? textLines(nextStep.text) : [];

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true" aria-labelledby="drawer-title">
        <div className="drawer-head">
          {/* Who and what on the left; what you can do with it on the right. */}
          <div className="drawer-title-row">
            <div className="drawer-titles">
              <h2 id="drawer-title">{opportunity.account_name ?? 'No account'}</h2>
              <p>{shortOpportunityName(opportunity.opp_name, opportunity.account_name)}</p>
              <p className="muted">Rep: {opportunity.rep.name}</p>
            </div>
            <div className="drawer-actions">
              <SalesforceButton href={opportunityUrl(instanceUrl, opportunity.opp_id)} />
              <button ref={closeButton} type="button" className="button" onClick={onClose} aria-label="Close">✕</button>
            </div>
          </div>

          {/* The Pipeline figure (the table hides that column while the drawer is open), where it
              stands and its dates. Every money figure is in the Money block below (GRO-247). */}
          <dl className="facts">
            <Fact label="Pipeline" value={<MoneyValue value={opportunity.pipeline_value} />} detail={pipelineDetail} wide />
            <Fact label="Close" value={formatDate(opportunity.close_date, today)} detail={opportunity.attention.past_close ? 'Past close' : undefined} late={opportunity.attention.past_close} />
            <Fact label="Stage" value={opportunity.stage} detail={`${opportunity.days_in_stage} days in stage`} wide />
            <Fact label="Opened" value={formatDate(opportunity.created_date, today)} />
            <Fact label="Last touch" value={touchValue} detail={touchBy ? touchBy.replace(/^ · /, '') : undefined} full />
          </dl>
        </div>

        <div className="drawer-body">
          <OwnersSection opportunity={opportunity} />
          <MoneySection opportunity={opportunity} />

          <section>
            <h3>Next step</h3>
            {repStepLines.length > 0 ? (
              <>
                {repStepLines.map((line, index) => <p key={index}>{index === 0 ? withoutLeadingDate(line) : line}</p>)}
                {nextStep.date ? <p className="muted">Noted {formatDate(nextStep.date, today)}</p> : null}
              </>
            ) : (
              <p className="level-bad">No next step in Salesforce.</p>
            )}
          </section>

          <LeadershipNextStepSection
            key={opportunity.opp_id}
            opportunity={opportunity}
            today={today}
            getToken={getToken}
            onSaved={onLeadershipSaved}
          />

          {gong ? (
            <section>
              <h3>Gong suggestion · call on {formatDate(gong.call_date, today)}</h3>
              <ul className="gong-list">
                {textLines(gong.text).map((line, index) => <li key={index}>{line}</li>)}
              </ul>
              <p className="muted">Gong's reading of the call, not the rep's next step.</p>
            </section>
          ) : null}

          <TouchHistorySection getToken={getToken} oppId={opportunity.opp_id} today={today} instanceUrl={instanceUrl} />
        </div>
      </aside>
    </>
  );
}
